import { Sidebar } from '@components/dashboard/superuser/Sidebar';
import { ACCOUNTING_SIDEBAR_SECTIONS } from '@components/dashboard/superuser/sidebarConfig';
import { useAppSelector } from '@hooks/useRedux';
import {
  selectAccessControlCodes,
  selectCurrentUser,
  selectHasUnrestrictedAccess,
} from '@store/auth/authSelectors';
import { Calculator } from 'lucide-react';
import React, { useState } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { OutletSelector } from '../components/common';
import { UserActionsMenu } from '../components/nav/UserActionsMenu';

export const AccountingPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const hasUnrestrictedAccess = useAppSelector(selectHasUnrestrictedAccess);
  const accessControlCodes = useAppSelector(selectAccessControlCodes);
  const canAccess = Boolean(
    hasUnrestrictedAccess ||
    accessControlCodes.some((code) => code.startsWith('accounting.')),
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (!canAccess) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Calculator size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-600">Access restricted</p>
          <p className="mt-1 text-xs text-gray-400">
            You do not have permission to access accounting.
          </p>
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="mt-4 text-xs text-pos-primary underline"
          >
            Back to sales
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans text-sm text-gray-900">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
        sections={ACCOUNTING_SIDEBAR_SECTIONS}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-gray-100 bg-white px-8 py-4">
          <div className="flex items-center gap-4">
            <Calculator size={32} className="text-pos-primary" />
            <div className="flex-1">
              <h1 className="text-base font-semibold text-gray-900">
                Accounting
              </h1>
              <p className="text-xs text-gray-400">
                Financial controls, ledgers, and reporting workspace.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <OutletSelector />
            <UserActionsMenu />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountingPage;
